import { z } from 'zod';
import { ApplicationSource } from './fieldSettings';

// Application status type
export type ApplicationStatus =
  | 'not_started'
  | 'in_progress'
  | 'awaiting_signature'
  | 'submitted'
  | 'approved'
  | 'rejected';

export type PipelineStatus =
  | 'lead'
  | 'phone'
  | 'offer'
  | 'underwriting'
  | 'documents'
  | 'approved'
  | 'onboarded'
  | 'failed_submission'
  | 'declined';

export interface BeneficialOwner {
  firstName: string;
  lastName: string;
  title?: string;
  dateOfBirth?: string;
  ssn?: string;
  driverLicense?: string;
  phone?: string;
  email?: string;
  ownershipPercentage: string;
  
  // New home address structure
  sameAsBusinessAddress?: boolean;
  homeAddress?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    fullAddress: string;
  };
  
  // Legacy fields for backwards compatibility
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface ProcessingHistory {
  averageTicket: number;
  cardPresentPercentage: number;
  currentProcessor: string;
  ecommercePercentage: number;
  hasBeenTerminated: 'yes' | 'no';
  highTicket: number;
  isCurrentlyProcessing: 'yes' | 'no';
  monthlyVolume: number;
  terminationExplanation?: string;
  salesDeliveryDays?: number;
  whenIsCustomerCharged?: 'order' | 'delivery' | 'shipment';
  orderTurnaroundTime?: number;
  advancedDeposits?: 'yes' | 'no';
  depositPct?: number;
}

export interface CompanyAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface PersonalInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  // personalAddress removed - address now collected in Corporation Info step
}

// IndividualInfo interface removed - fields moved to BeneficialOwner

export interface CorporationInfo {
  businessName: string;
  dba?: string;
  corporationType: string;
  businessStartDate: string;
  ein: string;
  website?: string;
  businessDescription?: string;
  sicCode?: string;
  sicDescription?: string;
  customerServiceEmail?: string;
  customerServicePhone?: string;
  businessAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    fullAddress: string;
  };
}


export interface BankDetails {
  bankName: string;
  routingNumber: string;
  accountNumber: string;
  accountType?: 'checking' | 'savings';
}

export interface IntegrationLogEntry {
  timestamp: string;
  source: string;
  status: 'success' | 'error';
  message: string;
  actor?: string;
  details?: string;
}

export interface Documents {
  bank_statements?: string[];
  drivers_license?: string[];
  voided_check?: string[];
  [key: string]: string[] | undefined;
}

export interface PricingProfile {
  processingFee: number; // Percentage (e.g., 2.9 for 2.9%)
  transactionFee: number; // Fixed amount in cents (e.g., 30 for $0.30)
}

export interface Pricing {
  online: PricingProfile;
  offline: PricingProfile;
  chargebackFee?: number;     // Dollar amount for chargeback fees
  monthlyMinimum?: number;    // Monthly minimum fee in dollars
}

export interface OtherSettings {
  merchantCategory?: string; // 'ecommerce' | 'moto' | 'retail' | 'restaurant' | 'hotel'
}

export interface MerchantApplicationData {
  // Application source tracking
  applicationSource?: ApplicationSource;
  
  // New structure with personal info and application type
  personalInfo?: PersonalInfo;
  applicationType?: 'individual' | 'corporation';
  corporationInfo?: CorporationInfo;
  
  processingHistory?: ProcessingHistory & {
    acceptOfflineCards?: boolean;     // NEW: offline card acceptance
    retailPercentage?: number;        // NEW: retail transaction %  
    onlinePercentage?: number;        // RENAMED from ecommercePercentage
    b2bPercentage?: number;          // NEW: B2B override (admin only)
    b2cPercentage?: number;          // NEW: B2C override (admin only) 
    bankruptcy?: boolean;             // NEW: bankruptcy history
  };
  beneficialOwners?: (BeneficialOwner & {
    title?: string;                   // ENHANCED: role dropdown
  })[];
  bankDetails?: BankDetails;
  documents?: Documents;
  pricing?: Pricing;
  pricingTemplateId?: string;  // Selected pricing template from banking partner
  selectedBankingPartnerId?: string;  // Banking partner for pricing template
  other?: OtherSettings;

  // PayArc integration fields
  payArcMerchantCode?: string;
  payArcSubmittedAt?: Date | { toDate: () => Date };
  
  // Metadata
  currentStep?: number;
  completedSteps?: number[];
  submittedAt?: Date;
  approvedAt?: Date;
  rejectedAt?: Date;
  rejectionReason?: string;
}

export interface MerchantApplication {
  id: string;
  companyId: string;
  userId: string;
  status: ApplicationStatus;
  pipelineStatus: PipelineStatus;
  data: MerchantApplicationData;
  applicationSource?: ApplicationSource; // Track source at top level too
  assignedBanks?: string[]; // Array of banking partner IDs
  merchantCode?: string; // PayArc merchant code for webhook tracking
  mid?: string; // PayArc Merchant ID (processing account number) - assigned when onboarded
  createdAt: Date | { toDate: () => Date };
  updatedAt: Date | { toDate: () => Date };
  submittedAt?: Date | { toDate: () => Date };
  onboardedAt?: Date | { toDate: () => Date };
  declinedAt?: Date | { toDate: () => Date };
  lastWebhookEvent?: {
    eventType: string;
    receivedAt: Date | { toDate: () => Date };
    merchantCode?: string;
    mid?: string;
    previousPipelineStatus?: PipelineStatus;
    previousStatus?: ApplicationStatus;
  };
}

// Zod schemas for validation
export const bankDetailsSchema = z.object({
  bankName: z.string().min(1, 'Bank name is required'),
  routingNumber: z.string()
    .length(9, 'Routing number must be exactly 9 digits')
    .regex(/^\d+$/, 'Routing number must be numeric'),
  accountNumber: z.string()
    .min(6, 'Account number must be at least 6 characters'),
  confirmAccountNumber: z.string().min(1, 'Please confirm your account number'),
  accountType: z.enum(['checking', 'savings']).optional(),
}).refine(
  (data) => data.accountNumber === data.confirmAccountNumber,
  {
    message: 'Account numbers must match',
    path: ['confirmAccountNumber']
  }
);

export const businessInfoSchema = z.object({
  businessName: z.string().min(1, 'Business name is required'),
  dba: z.string().optional(),
  businessType: z.string().min(1, 'Business type is required'),
  businessDescription: z.string().min(1, 'Business description is required'),
  taxId: z.string().min(1, 'Tax ID is required'),
  yearEstablished: z.string().min(1, 'Year established is required'),
  website: z.string().optional(),
  customerServiceEmail: z.string().email('Invalid email address'),
  customerServicePhone: z.string().min(1, 'Customer service phone is required'),
  companyAddress: z.object({
    street: z.string().min(1, 'Street address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zipCode: z.string().min(1, 'ZIP code is required'),
  }),
});

export const processingHistorySchema = z.object({
  isCurrentlyProcessing: z.enum(['yes', 'no']),
  monthlyVolume: z.number().min(0),
  averageTicket: z.number().min(0),
  highTicket: z.number().min(0),
  salesDeliveryDays: z.number().min(0).optional(),
  whenIsCustomerCharged: z.enum(['order', 'delivery', 'shipment']).optional(),
  orderTurnaroundTime: z.number().min(0).optional(),
  advancedDeposits: z.enum(['yes', 'no']).optional(),
  depositPct: z.number().min(0).max(100).optional(),
  currentProcessor: z.string().optional(),
  cardPresentPercentage: z.number().min(0).max(100),
  ecommercePercentage: z.number().min(0).max(100),
  hasBeenTerminated: z.enum(['yes', 'no']),
  terminationExplanation: z.string().optional(),
});

// Step definition types
export interface ApplicationStep {
  id: number;
  title: string;
  description: string;
  conditional?: boolean;
  applicationType?: 'individual' | 'corporation';
}

// Step definitions
export const APPLICATION_STEPS: ApplicationStep[] = [
  {
    id: 1,
    title: 'Personal Information',
    description: 'Tell us about yourself',
  },
  {
    id: 2,
    title: 'Individual Info',
    description: 'Driver\'s license, SSN, and DOB',
    conditional: true,
    applicationType: 'individual',
  },
  {
    id: 3,
    title: 'Corporation Info',
    description: 'Consolidated business details, industry classification, and contact info',
    // Not conditional - both individuals and corporations go through this step
  },
  {
    id: 4,
    title: 'Processing History',
    description: 'Your payment processing experience',
  },
  {
    id: 5,
    title: 'Beneficial Owners',
    description: 'Information about owners with 25% or greater ownership',
  },
  {
    id: 6,
    title: 'Bank Details',
    description: 'Your settlement account information',
  },
  {
    id: 7,
    title: 'Documentation',
    description: 'Required business documents',
  },
];

// Helper function to get steps based on application type
export const getApplicationSteps = (applicationType?: 'individual' | 'corporation') => {
  return APPLICATION_STEPS.filter(step => {
    // Remove Individual Info step entirely; collect into Beneficial Owners instead
    if (step.title === 'Individual Info') return false;
    if (!step.conditional) return true;
    return step.applicationType === applicationType;
  });
};
